import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, Skeleton, Button, Paper,
  TextField, MenuItem, FormControl, InputLabel, Select,
  FormGroup, FormControlLabel, Checkbox, InputBase, IconButton,
  Chip, Collapse, Popover, Divider, useMediaQuery, useTheme, CircularProgress,
} from '@mui/material';
import {
  LocationOn, Search, Close, EmojiEvents, Today, Tune,
  LocalOffer, AccessTime, DirectionsCar, NearMe,
} from '@mui/icons-material';
import api from '../utils/api';
import LibraryCard from '../components/LibraryCard';
import { useGeolocation } from '../hooks/useGeolocation';
import DesktopNav from '../components/DesktopNav';
import StudentBottomNav from '../components/StudentBottomNav';
import FilterPill from '../components/shared/FilterPill';

const AMENITIES_LIST = ['24/7 Access', 'Parking', 'Discussion Rooms', 'Cafeteria'];
// Amenities surfaced as quick-filter pills on mobile; the rest live in the "More" panel
const PILL_AMENITIES = ['24/7 Access', 'Parking'];
const SHEET_AMENITIES = ['Discussion Rooms', 'Cafeteria'];

const DEFAULT_FILTERS = {
  minPrice: '',
  maxPrice: '',
  amenities: [],
  sortBy: 'name',
  topRated: false,
  minDiscount: '',
  oneDayPass: false,
  nearby: false,
};

function SeoLocationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Extract slug from URL path: /library-in-uttam_nagar → uttam_nagar
  const slug = location.pathname.replace('/library-in-', '');

  const [pageData, setPageData] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);     // initial page load
  const [gridLoading, setGridLoading] = useState(false); // filter refetch
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false); // mobile filter panel

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Geolocation for the "Nearby" filter
  const {
    coordinates,
    loading: geoLoading,
    error: geoError,
    requestPermission,
  } = useGeolocation();

  // Mobile "Offers" pill popover anchor
  const offersRef = useRef(null);
  const [offersAnchor, setOffersAnchor] = useState(null);

  // Guard against out-of-order responses from rapid filter changes
  const requestIdRef = useRef(0);
  const pageLoadedRef = useRef(false);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchPageData = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    try {
      if (!pageLoadedRef.current) setLoading(true);
      else setGridLoading(true);
      setError(null);

      const params = {
        search: debouncedSearch || undefined,
        minPrice: filters.minPrice || undefined,
        maxPrice: filters.maxPrice || undefined,
        sortBy: filters.sortBy,
        topRated: filters.topRated ? 'true' : undefined,
        minDiscount: filters.minDiscount || undefined,
        oneDayPass: filters.oneDayPass ? 'true' : undefined,
      };
      if (filters.amenities.length > 0) params.amenities = filters.amenities;
      // Nearby: only send geo once we actually have the user's coordinates.
      if (filters.nearby && coordinates) {
        params.nearby = 'true';
        params.userLat = coordinates.latitude;
        params.userLng = coordinates.longitude;
      }

      const response = await api.get(`/seo/public/${slug}`, { params });
      if (reqId !== requestIdRef.current) return; // a newer request superseded this one

      setPageData(response.data.data.page);
      setLibraries(response.data.data.libraries);
      setFeatured(response.data.data.featured || []);
      pageLoadedRef.current = true;
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      if (err.response?.status === 404) {
        setError('Page not found');
      } else {
        setError('Failed to load page');
      }
    } finally {
      if (reqId === requestIdRef.current) {
        setLoading(false);
        setGridLoading(false);
      }
    }
  }, [slug, debouncedSearch, filters, coordinates]);

  useEffect(() => {
    if (slug) fetchPageData();
  }, [fetchPageData, slug]);

  // Toggle the "Nearby" filter; request location permission the first time it's on.
  const handleNearbyToggle = () => {
    const turningOn = !filters.nearby;
    if (turningOn && !coordinates) requestPermission();
    setFilters((prev) => ({ ...prev, nearby: !prev.nearby }));
  };

  // Reset state when navigating to a different SEO page
  useEffect(() => {
    pageLoadedRef.current = false;
    setSearchInput('');
    setDebouncedSearch('');
    setFilters(DEFAULT_FILTERS);
    setFeatured([]);
  }, [slug]);

  // Set document meta tags
  useEffect(() => {
    if (!pageData) return;

    if (pageData.meta_title) {
      document.title = pageData.meta_title;
    }

    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', pageData.meta_description);
    setMeta('keywords', pageData.meta_keywords);

    return () => {
      document.title = 'BookMyLibrary';
    };
  }, [pageData]);

  const handleAmenityToggle = (amenity) => {
    setFilters((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  };

  const activeFiltersCount = [
    filters.minPrice,
    filters.maxPrice,
    filters.amenities.length > 0,
    filters.topRated,
    filters.minDiscount,
    filters.oneDayPass,
    filters.nearby,
    filters.sortBy !== 'name',
  ].filter(Boolean).length;

  // Count of filters tucked behind the mobile "More" pill (everything not a pill)
  const moreBadgeCount = [
    filters.minPrice,
    filters.maxPrice,
    filters.sortBy !== 'name',
    filters.amenities.some((a) => SHEET_AMENITIES.includes(a)),
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
  };

  const handleOffersPillClick = () => {
    if (filters.minDiscount) {
      setOffersAnchor(offersRef.current);
    } else {
      setFilters((prev) => ({ ...prev, minDiscount: '10' }));
    }
  };

  const renderFilterContent = () => (
    <Box>
      {activeFiltersCount > 0 && (
        <Button size="small" onClick={clearAllFilters} sx={{ textTransform: 'none', mb: 2 }}>
          Clear All
        </Button>
      )}

      {/* Top Rated */}
      <Button
        fullWidth
        variant={filters.topRated ? 'contained' : 'outlined'}
        startIcon={<EmojiEvents />}
        onClick={() => setFilters((prev) => ({ ...prev, topRated: !prev.topRated }))}
        sx={{ mb: 2, borderRadius: 3, py: 1.5, fontWeight: 600, textTransform: 'none' }}
      >
        {filters.topRated ? 'Showing Top Rated' : 'Top Rated Only'}
      </Button>

      {/* Nearby (geolocation) */}
      <Button
        fullWidth
        variant={filters.nearby ? 'contained' : 'outlined'}
        startIcon={geoLoading && filters.nearby ? <CircularProgress size={18} color="inherit" /> : <NearMe />}
        onClick={handleNearbyToggle}
        sx={{ mb: filters.nearby && geoError ? 1 : 2, borderRadius: 3, py: 1.5, fontWeight: 600, textTransform: 'none' }}
      >
        {geoLoading && filters.nearby
          ? 'Locating…'
          : filters.nearby
            ? 'Showing Nearby'
            : 'Nearby'}
      </Button>
      {filters.nearby && geoError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
          {geoError.message}
        </Typography>
      )}

      {/* One Day Pass */}
      <Button
        fullWidth
        variant={filters.oneDayPass ? 'contained' : 'outlined'}
        startIcon={<Today />}
        onClick={() => setFilters((prev) => ({ ...prev, oneDayPass: !prev.oneDayPass }))}
        sx={{ mb: 3, borderRadius: 3, py: 1.5, fontWeight: 600, textTransform: 'none' }}
      >
        {filters.oneDayPass ? 'Showing Day Pass' : 'Day Pass Available'}
      </Button>

      {/* Minimum Discount */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Minimum Discount</InputLabel>
        <Select
          value={filters.minDiscount}
          label="Minimum Discount"
          onChange={(e) => setFilters((prev) => ({ ...prev, minDiscount: e.target.value }))}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="10">10% or more</MenuItem>
          <MenuItem value="20">20% or more</MenuItem>
          <MenuItem value="30">30% or more</MenuItem>
          <MenuItem value="40">40% or more</MenuItem>
        </Select>
      </FormControl>

      {/* Price range */}
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Price Range (₹)
      </Typography>
      <Box display="flex" gap={1.5} sx={{ mb: 3 }}>
        <TextField
          label="Min"
          type="number"
          size="small"
          value={filters.minPrice}
          onChange={(e) => setFilters((prev) => ({ ...prev, minPrice: e.target.value }))}
          fullWidth
        />
        <TextField
          label="Max"
          type="number"
          size="small"
          value={filters.maxPrice}
          onChange={(e) => setFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}
          fullWidth
        />
      </Box>

      {/* Sort By */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={filters.sortBy}
          label="Sort By"
          onChange={(e) => setFilters((prev) => ({ ...prev, sortBy: e.target.value }))}
        >
          <MenuItem value="name">Name</MenuItem>
          <MenuItem value="price">Price (Low to High)</MenuItem>
        </Select>
      </FormControl>

      {/* Amenities */}
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Amenities
      </Typography>
      <FormGroup>
        {AMENITIES_LIST.map((amenity) => (
          <FormControlLabel
            key={amenity}
            control={
              <Checkbox
                checked={filters.amenities.includes(amenity)}
                onChange={() => handleAmenityToggle(amenity)}
              />
            }
            label={amenity}
          />
        ))}
      </FormGroup>
    </Box>
  );

  // Reduced filter set behind the mobile "More" pill (price, sort, non-pill amenities)
  const renderMoreContent = () => (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Price Range (₹)
      </Typography>
      <Box display="flex" gap={1.5} sx={{ mb: 3 }}>
        <TextField
          label="Min"
          type="number"
          size="small"
          value={filters.minPrice}
          onChange={(e) => setFilters((prev) => ({ ...prev, minPrice: e.target.value }))}
          fullWidth
        />
        <TextField
          label="Max"
          type="number"
          size="small"
          value={filters.maxPrice}
          onChange={(e) => setFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}
          fullWidth
        />
      </Box>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={filters.sortBy}
          label="Sort By"
          onChange={(e) => setFilters((prev) => ({ ...prev, sortBy: e.target.value }))}
        >
          <MenuItem value="name">Name</MenuItem>
          <MenuItem value="price">Price (Low to High)</MenuItem>
        </Select>
      </FormControl>

      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        More Amenities
      </Typography>
      <FormGroup>
        {SHEET_AMENITIES.map((amenity) => (
          <FormControlLabel
            key={amenity}
            control={
              <Checkbox
                checked={filters.amenities.includes(amenity)}
                onChange={() => handleAmenityToggle(amenity)}
              />
            }
            label={amenity}
          />
        ))}
      </FormGroup>
    </Box>
  );

  // Mobile quick-filter pill strip — matches the library search page look
  const renderMobilePills = () => (
    <Box sx={{ mt: 1.5 }}>
      <Box
        component="nav"
        aria-label="Quick filters"
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          flexWrap: 'nowrap',
          pb: 0.5,
        }}
      >
        <FilterPill
          label="Top Rated"
          icon={<EmojiEvents fontSize="small" />}
          active={filters.topRated}
          onClick={() => setFilters((prev) => ({ ...prev, topRated: !prev.topRated }))}
        />
        <FilterPill
          label={geoLoading && filters.nearby ? 'Locating…' : 'Nearby'}
          icon={<NearMe fontSize="small" />}
          active={filters.nearby}
          onClick={handleNearbyToggle}
        />
        <FilterPill
          label="Day Pass"
          icon={<Today fontSize="small" />}
          active={filters.oneDayPass}
          onClick={() => setFilters((prev) => ({ ...prev, oneDayPass: !prev.oneDayPass }))}
        />
        <Box ref={offersRef} sx={{ flexShrink: 0 }}>
          <FilterPill
            label={filters.minDiscount ? `Offers · ${filters.minDiscount}%+` : 'Offers'}
            icon={<LocalOffer fontSize="small" />}
            active={!!filters.minDiscount}
            hasPopover
            onClick={handleOffersPillClick}
          />
        </Box>
        {PILL_AMENITIES.map((amenity) => (
          <FilterPill
            key={amenity}
            label={amenity === '24/7 Access' ? '24/7' : amenity}
            icon={amenity === '24/7 Access' ? <AccessTime fontSize="small" /> : <DirectionsCar fontSize="small" />}
            active={filters.amenities.includes(amenity)}
            onClick={() => handleAmenityToggle(amenity)}
          />
        ))}
        <FilterPill
          label={moreBadgeCount > 0 ? `More · ${moreBadgeCount}` : 'More'}
          icon={<Tune fontSize="small" />}
          active={false}
          onClick={() => setFilterOpen((o) => !o)}
          ariaLabel={`More filters${moreBadgeCount ? `, ${moreBadgeCount} active` : ''}`}
        />
      </Box>

      {/* Active non-pill filter chips (price + non-pill amenities) */}
      {(filters.minPrice || filters.maxPrice || filters.amenities.some((a) => SHEET_AMENITIES.includes(a))) && (
        <Box display="flex" gap={1} flexWrap="wrap" mt={1.5}>
          {filters.minPrice && (
            <Chip size="small" label={`Min ₹${filters.minPrice}`} onDelete={() => setFilters((prev) => ({ ...prev, minPrice: '' }))} />
          )}
          {filters.maxPrice && (
            <Chip size="small" label={`Max ₹${filters.maxPrice}`} onDelete={() => setFilters((prev) => ({ ...prev, maxPrice: '' }))} />
          )}
          {filters.amenities.filter((a) => SHEET_AMENITIES.includes(a)).map((a) => (
            <Chip key={a} size="small" label={a} onDelete={() => handleAmenityToggle(a)} />
          ))}
        </Box>
      )}

      {/* "More" panel */}
      <Collapse in={filterOpen} unmountOnExit>
        <Paper sx={{ borderRadius: 3, p: 2.5, mt: 1.5 }}>
          {renderMoreContent()}
        </Paper>
      </Collapse>

      {/* Offers popover — discount picker */}
      <Popover
        open={Boolean(offersAnchor)}
        anchorEl={offersAnchor}
        onClose={() => setOffersAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 1, p: 2, borderRadius: 3, minWidth: 220 } } }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
          Minimum discount
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
          {[10, 20, 30, 40].map((pct) => (
            <Chip
              key={pct}
              label={`${pct}%`}
              color={filters.minDiscount === String(pct) ? 'primary' : 'default'}
              variant={filters.minDiscount === String(pct) ? 'filled' : 'outlined'}
              onClick={() => setFilters((prev) => ({ ...prev, minDiscount: String(pct) }))}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
        <Divider sx={{ my: 1 }} />
        <Button
          size="small"
          sx={{ textTransform: 'none' }}
          onClick={() => {
            setFilters((prev) => ({ ...prev, minDiscount: '' }));
            setOffersAnchor(null);
          }}
        >
          Clear offer
        </Button>
      </Popover>
    </Box>
  );

  const SearchBar = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.subtle',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Box display="flex" alignItems="center" px={2}>
        <Search sx={{ color: 'text.secondary', mr: 1 }} />
        <InputBase
          fullWidth
          placeholder="Search by name or location..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ py: 1.5, fontSize: '0.95rem' }}
        />
        {searchInput && (
          <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Paper>
  );

  const renderLibrariesGrid = () => {
    if (gridLoading) {
      return (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={340} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (libraries.length === 0) {
      return (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            {activeFiltersCount > 0 || debouncedSearch
              ? 'No libraries match your filters'
              : 'No libraries found in this area yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {activeFiltersCount > 0 || debouncedSearch
              ? 'Try adjusting or clearing your filters.'
              : "We're expanding! Check back soon or browse all available libraries."}
          </Typography>
          {activeFiltersCount > 0 || debouncedSearch ? (
            <Button variant="outlined" onClick={clearAllFilters} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Clear Filters
            </Button>
          ) : (
            <Button variant="outlined" onClick={() => navigate('/libraries')} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Browse All Libraries
            </Button>
          )}
        </Paper>
      );
    }

    return (
      <Grid container spacing={3}>
        {libraries.map((library) => (
          <Grid item xs={12} sm={6} md={isDesktop ? 4 : 6} key={library.id}>
            <LibraryCard library={library} />
          </Grid>
        ))}
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <DesktopNav />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Skeleton variant="text" width={300} height={50} />
          <Skeleton variant="text" width={500} height={30} sx={{ mb: 4 }} />
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <DesktopNav />
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            {error === 'Page not found' ? '404 - Page Not Found' : 'Error'}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            {error === 'Page not found'
              ? 'The page you are looking for does not exist.'
              : 'Something went wrong. Please try again.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/libraries')} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Browse All Libraries
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: { xs: 10, md: 0 } }}>
      <DesktopNav />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
          color: 'white',
          py: { xs: 1.5, md: 2 },
        }}
      >
        <Container maxWidth="xl">
          <Typography variant="h5" fontWeight="800" sx={{ mb: 0.25, fontSize: { xs: '1.15rem', md: '1.5rem' } }}>
            Libraries in {pageData.location_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.9 }}>
            <LocationOn sx={{ fontSize: 16 }} />
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
              {libraries.length} {libraries.length === 1 ? 'library' : 'libraries'} found in {pageData.location_name}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Featured libraries carousel — only shown when the connected set has entries ── */}
      {featured.length > 0 && (
        <Box
          sx={{
            bgcolor: 'background.default',
            pt: { xs: 1.5, md: 2 },
            pb: 0,
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <EmojiEvents sx={{ color: '#F26522', fontSize: '1.2rem' }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Recommended by BookMyLibrary
              </Typography>
            </Box>
            <Box
              component="ul"
              aria-label="Featured libraries"
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                flexWrap: 'nowrap',
                pb: 1,
                m: 0,
                p: 0,
                listStyle: 'none',
              }}
            >
              {featured.map((lib) => (
                <Box
                  component="li"
                  key={lib.id}
                  sx={{ flex: '0 0 auto', width: { xs: 180, sm: 200 } }}
                >
                  <LibraryCard library={lib} compact />
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      )}

      <Container maxWidth="xl" sx={{ pt: { xs: 1, md: 1.5 }, pb: 4 }}>
        {/* Search + mobile quick-filter pills */}
        <Box mb={3}>
          {SearchBar}
          {!isDesktop && renderMobilePills()}
        </Box>

        <Grid container spacing={3}>
          {/* Desktop sidebar */}
          {isDesktop && (
            <Grid item md={3}>
              <Paper
                sx={{
                  borderRadius: 3,
                  p: 3,
                  position: 'sticky',
                  top: 90,
                  maxHeight: 'calc(100vh - 110px)',
                  overflowY: 'auto',
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Filters
                </Typography>
                {renderFilterContent()}
              </Paper>
            </Grid>
          )}

          {/* Libraries grid */}
          <Grid item xs={12} md={isDesktop ? 9 : 12}>
            {renderLibrariesGrid()}

            {/* Page Content */}
            {pageData.page_content && (
              <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, mt: 4 }}>
                <div dangerouslySetInnerHTML={{ __html: pageData.page_content }} />
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>

      <StudentBottomNav />
    </Box>
  );
}

export default SeoLocationPage;
